import { IAuthenticationProvider, AuthenticationResult, AuthConfig, AuthenticationProvider } from "./IAuthenticationProvider";
import { Auth0Client } from '@auth0/auth0-spa-js';
import { Auth0Account } from "./Auth0Account";
import { IAccount } from "./IAccount";

interface Auth0Config {
    domain: string;
    clientId: string;
    audience: string;
}

interface Auth0User {
    email?: string;
    name?: string;
    sub?: string;
    [key: string]: any;
}

export class Auth0AuthenticationProvider implements IAuthenticationProvider {
    private auth0Client: Auth0Client | null = null;
    private config: Auth0Config;
    private accessToken: string | null = null;
    private user: Auth0User | null = null;

    constructor(config: Auth0Config) {
        this.config = config;
    }

    async initialize(): Promise<void> {
        if (this.auth0Client) {
            return;
        }

        try {
            this.auth0Client = new Auth0Client({
                domain: this.config.domain,
                clientId: this.config.clientId,
                authorizationParams: {
                    audience: this.config.audience,
                    redirect_uri: `${window.location.origin}/callback`,
                    scope: 'openid email profile offline_access'
                },
                cacheLocation: 'localstorage',
                useRefreshTokens: true
            });

            // Check if we're returning from Auth0 callback
            await this.handleRedirectCallback();

        } catch (error) {
            console.error('Error initializing Auth0:', error);
            throw new Error(`Auth0 initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    async handleRedirectCallback(): Promise<void> {
        if (!this.auth0Client) {
            throw new Error('Auth0 client not initialized');
        }

        try {
            // Handle the callback if we're on the callback URL
            const searchParams = new URLSearchParams(window.location.search);
            if (searchParams.get('code') && searchParams.get('state')) {
                const result = await this.auth0Client.handleRedirectCallback();
                window.history.replaceState({}, document.title, window.location.pathname);
                await this.getTokenSilent();
                const targetUrl = result.appState && result.appState.targetUrl
                    ? result.appState.targetUrl
                    : null;
                if (targetUrl && targetUrl !== window.location.href) {
                    console.log("Redirecting to target URL:", targetUrl);
                    window.location.href = targetUrl;
                }
            }
        } catch (error) {
            console.error('Auth0 redirect callback error:', error);
        }
    }



    async getTokenSilent(forceRefresh: boolean = false): Promise<AuthenticationResult> {
        if (!this.auth0Client) {
            await this.initialize();
        }

        try {
            const options: any = { detailedResponse: true };
            if (forceRefresh) {
                options.cacheMode = 'off';
            }

            const token = await this.auth0Client!.getTokenSilently(options);
            const user = await this.auth0Client!.getUser();
            if (token) {
                this.accessToken = token.access_token;
                this.user = user || null;
                const account = Auth0Account.fromAuth0User(user);

                return {
                    accessToken: token.access_token,
                    account: account,
                    tenantId: null,
                    success: true
                };
            } else {
                return {
                    accessToken: null,
                    account: null,
                    tenantId: null,
                    success: false,
                    error: 'No token received'
                };
            }
        } catch (error: any) {
            console.error('Auth0 getTokenSilent error:', error);

            // If token refresh fails, user might need to re-authenticate
            if (error.error === 'login_required' || error.error === 'consent_required') {
                return {
                    accessToken: null,
                    account: null,
                    tenantId: null,
                    success: false,
                    error: 'Re-authentication required'
                };
            }

            return {
                accessToken: null,
                account: null,
                tenantId: null,
                success: false,
                error: error.message || 'Token acquisition failed'
            };
        }
    }

    async login(loginState?: string): Promise<void> {
        if (!this.auth0Client) {
            await this.initialize();
        }

        if (loginState) {
            localStorage.setItem("FluenceLoginState", loginState);
        }
        try {
            await this.auth0Client!.loginWithRedirect({
                appState: { targetUrl: window.location.href },
                authorizationParams: {
                    audience: this.config.audience,
                    redirect_uri: `${window.location.origin}/callback`,
                    scope: 'openid profile email offline_access'
                }
            });
        } catch (error) {
            console.error('Auth0 login error:', error);
            throw error;
        }
    }

    async forceLogin(): Promise<void> {
        if (!this.auth0Client) {
            await this.initialize();
        }

        try {
            await this.auth0Client!.loginWithRedirect({
                appState: { targetUrl: window.location.href },
                authorizationParams: {
                    audience: this.config.audience,
                    redirect_uri: `${window.location.origin}/callback`,
                    scope: 'openid profile email offline_access',
                    prompt: 'select_account'
                }
            });
        } catch (error) {
            console.error('Auth0 force login error:', error);
            throw error;
        }
    }

    async logout(): Promise<void> {
        if (!this.auth0Client) {
            return;
        }

        this.accessToken = null;
        this.user = null;

        // Token cleared from memory - no cookie to clear

        // Clear localStorage items
        localStorage.removeItem("tenantWebApiUrl");
        localStorage.removeItem("tenantName");
        localStorage.removeItem("FluenceLoginState");

        try {
            await this.auth0Client.logout({
                logoutParams: {
                    returnTo: `${window.location.origin}`
                }
            });
        } catch (error) {
            console.error('Auth0 logout error:', error);
        }
    }



    getAccount(): IAccount | null {
        if (!this.user) {
            return null;
        }

        // Create Auth0Account from current user data
        return Auth0Account.fromAuth0User(this.user);
    }

    getAccessToken(): string | null {
        return this.accessToken;
    }

    getTenantId(): string | null {
        // Auth0 doesn't use tenant IDs in the same way as Azure AD
        return null;
    }

    getProviderType(): AuthenticationProvider {
        return AuthenticationProvider.AUTH0;
    }


}
